export const DEFAULT_AI_MODEL = 'gemini-3.8-flash';

export const AI_MODEL_OPTIONS = [
  { value: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash', description: 'Default model', icon: 'flash' },
  { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro Preview', description: 'Preview model', icon: 'preview' },
  { value: 'gemini-4-argon', label: 'Gemini 4 Argon', description: 'Argon model', icon: 'argon' },
] as const;

// Empty, spaces, or the text null/nill (any case) all mean "no model".
export const isBlankModel = (model?: string | null) => {
  const v = model?.trim().toLowerCase();
  return !v || v === 'null' || v === 'nill';
};

export const modelForEnabledAi = (model?: string | null) => isBlankModel(model) ? DEFAULT_AI_MODEL : model!.trim();

export interface AiSettingsWriter {
  saveModel: (model: string | null) => Promise<void>;
  setEnabled: (enabled: boolean) => Promise<void>;
  /** Optional read-back of the saved model after enabling; an empty value is re-saved. */
  readModel?: () => Promise<string | null | undefined>;
}

// A model must be persisted before AI activation. Failures deliberately propagate.
export async function changeAiEnabled(enabled: boolean, model: string | null, writer: AiSettingsWriter) {
  if (enabled) {
    const selected = modelForEnabledAi(model);
    await writer.saveModel(selected);
    await writer.setEnabled(true);
    if (writer.readModel && isBlankModel(await writer.readModel())) {
      await writer.saveModel(selected);
    }
    return selected;
  }
  await writer.setEnabled(false);
  await writer.saveModel(null);
  return null;
}
// A library query's saved model only applies while AI is on and when it differs.
export const modelToApplyFromTemplate = (aiEnabled: boolean, current: string | null, templateModel?: string | null) => {
  const next = templateModel?.trim();
  if (!aiEnabled || !next || next === current?.trim()) return null;
  return next;
};

// BuildShip clears a field when it receives the text 'null'.
export const BUILDSHIP_NULL = 'null';
export const buildshipModelValue = (model: string | null) => model ?? BUILDSHIP_NULL;

// Legacy repair only for cameras that are on in both the saved record and the screen.
export const needsModelRepair = (o: { recordEnabled?: boolean | null; localEnabled: boolean; model?: string | null; workflow?: string }) =>
  o.recordEnabled === true && o.localEnabled && isBlankModel(o.model) && o.workflow !== 'custom_workflow';
